import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Alert,
  Spinner,
} from 'react-bootstrap';
import AppScriptAPI from '../../services/appscriptAPI';
import { useUserPermissions } from '../../contexts/UserPermissionsContext';
import { filterLocationData, allowedAnalyticsLevels } from '../../utils/accessControl';

function AnalyticsSelection() {
  const navigate = useNavigate();
  const { userPermissions } = useUserPermissions();

  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });

  const [locationData, setLocationData] = useState({
    provinces: [],
    districts: {},
    divisions: {},
    sheetIds: {},
  });

  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedProvince, setSelectedProvince] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedDivision, setSelectedDivision] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        // Load location data
        const locData = await AppScriptAPI.getLocationDataForAnalytics();

        // Only update state if component is still mounted
        if (isMounted) {
          setLocationData(filterLocationData(locData, userPermissions));
          setLoading(false);
        }
      } catch (error) {
        if (isMounted) {
          setAlert({
            show: true,
            message: 'Failed to load data: ' + error.message,
            type: 'danger',
          });
          setLoading(false);
        }
      }
    };

    loadData();

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array is correct here

  const handleLevelChange = (level) => {
    setSelectedLevel(level);
    setSelectedProvince('');
    setSelectedDistrict('');
    setSelectedDivision('');
    setAlert({ show: false, message: '', type: '' });
  };

  const handleProvinceChange = (e) => {
    const provinceCode = e.target.value;
    setSelectedProvince(provinceCode);
    setSelectedDistrict('');
    setSelectedDivision('');
  };

  const handleDistrictChange = (e) => {
    const districtCode = e.target.value;
    setSelectedDistrict(districtCode);
    setSelectedDivision('');
  };

  const isSelectionComplete = () => {
    if (!selectedLevel) return false;
    if (selectedLevel === 'all-island') return true;
    if (selectedLevel === 'province') return !!selectedProvince;
    if (selectedLevel === 'district')
      return !!selectedProvince && !!selectedDistrict;
    if (selectedLevel === 'ds-division')
      return !!selectedProvince && !!selectedDistrict && !!selectedDivision;
    return false;
  };

  const handleViewAnalytics = () => {
    if (!isSelectionComplete()) {
      setAlert({
        show: true,
        message: 'Please complete your selection.',
        type: 'warning',
      });
      return;
    }

    let sheetId = '';
    let code = '';

    // Get appropriate sheet ID and code based on selection
    if (selectedLevel === 'all-island') {
      code = '';
    } else if (selectedLevel === 'province') {
      code = selectedProvince;
    } else if (selectedLevel === 'district') {
      code = selectedDistrict;
    } else if (selectedLevel === 'ds-division') {
      // selectedDivision is already the combined key "districtCode_dsdCode" (e.g. "11_03")
      // because division.code was changed to this format in getLocationDataForAnalytics
      code = selectedDivision;
      sheetId = locationData.sheetIds?.[selectedDivision];

      if (!sheetId) {
        setAlert({
          show: true,
          message: 'Sheet ID not configured for this DS division.',
          type: 'danger',
        });
        return;
      }
    }

    // Navigate to analytics view
    navigate(
      `/analytics/view?level=${selectedLevel}&code=${code}&sheetId=${sheetId}`
    );
  };

  const getSelectedNames = () => {
    const names = [];

    if (selectedLevel) {
      names.push({ label: 'Level', value: formatLevel(selectedLevel) });
    }

    if (selectedProvince) {
      const province = locationData.provinces.find(
        (p) => p.code === selectedProvince
      );
      if (province) names.push({ label: 'Province', value: province.name });
    }

    if (selectedDistrict) {
      const district = locationData.districts[selectedProvince]?.find(
        (d) => d.code === selectedDistrict
      );
      if (district) names.push({ label: 'District', value: district.name });
    }

    if (selectedDivision) {
      const division = locationData.divisions[selectedDistrict]?.find(
        (d) => d.code === selectedDivision
      );
      if (division) names.push({ label: 'DS Division', value: division.name });
    }

    return names;
  };

  const formatLevel = (level) => {
    const map = {
      'all-island': 'All Island',
      province: 'Province',
      district: 'District',
      'ds-division': 'DS Division',
    };
    return map[level] || level;
  };

  const userAllowedLevels = allowedAnalyticsLevels(userPermissions);

  if (loading) {
    return (
      <Container className="mt-5">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-5" style={{ minHeight: '100vh' }}>
      <Row className="justify-content-center">
        <Col xs={12} lg={8} xl={6}>
          {/* Header Card */}
          <div
            className="selection-card text-center text-white"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '15px',
              padding: '40px',
              marginBottom: '30px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
            }}
          >
            <h2><i className="bi bi-bar-chart-fill me-2"></i>Analytics Selection</h2>
            <p className="mt-3 mb-0">
              Choose your analytics level and location to view detailed
              statistics
            </p>
          </div>

          {/* Error Alert */}
          {alert.show && (
            <Alert
              variant={alert.type}
              dismissible
              onClose={() => setAlert({ ...alert, show: false })}
            >
              {alert.message}
            </Alert>
          )}

          {/* Selection Form */}
          <div
            style={{
              background: 'white',
              borderRadius: '10px',
              padding: '30px',
              marginBottom: '30px',
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
            }}
          >
            <h5 className="mb-4">Select Analytics Level</h5>

            {/* Level Cards */}
            <Row>
              {userAllowedLevels.includes('all-island') && <Col md={6} className="mb-3">
                <div
                  className={`level-option-card ${
                    selectedLevel === 'all-island' ? 'selected' : ''
                  }`}
                  onClick={() => handleLevelChange('all-island')}
                  style={{
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    border:
                      selectedLevel === 'all-island'
                        ? '2px solid #667eea'
                        : '2px solid #e9ecef',
                    borderRadius: '10px',
                    padding: '20px',
                    background:
                      selectedLevel === 'all-island'
                        ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
                        : 'white',
                  }}
                >
                  <div className="text-center">
                    <div style={{ fontSize: '2rem', marginBottom: '10px' }}>
                      <i className="bi bi-globe2"></i>
                    </div>
                    <h6 className="fw-bold">All Island</h6>
                    <p className="small text-muted mb-0">
                      View nationwide statistics
                    </p>
                  </div>
                </div>
              </Col>}

              {userAllowedLevels.includes('province') && <Col md={6} className="mb-3">
                <div
                  className={`level-option-card ${
                    selectedLevel === 'province' ? 'selected' : ''
                  }`}
                  onClick={() => handleLevelChange('province')}
                  style={{
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    border:
                      selectedLevel === 'province'
                        ? '2px solid #667eea'
                        : '2px solid #e9ecef',
                    borderRadius: '10px',
                    padding: '20px',
                    background:
                      selectedLevel === 'province'
                        ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
                        : 'white',
                  }}
                >
                  <div className="text-center">
                    <div style={{ fontSize: '2rem', marginBottom: '10px' }}>
                      <i className="bi bi-building"></i>
                    </div>
                    <h6 className="fw-bold">Province</h6>
                    <p className="small text-muted mb-0">
                      Provincial level data
                    </p>
                  </div>
                </div>
              </Col>}

              {userAllowedLevels.includes('district') && <Col md={6} className="mb-3">
                <div
                  className={`level-option-card ${
                    selectedLevel === 'district' ? 'selected' : ''
                  }`}
                  onClick={() => handleLevelChange('district')}
                  style={{
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    border:
                      selectedLevel === 'district'
                        ? '2px solid #667eea'
                        : '2px solid #e9ecef',
                    borderRadius: '10px',
                    padding: '20px',
                    background:
                      selectedLevel === 'district'
                        ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
                        : 'white',
                  }}
                >
                  <div className="text-center">
                    <div style={{ fontSize: '2rem', marginBottom: '10px' }}>
                      <i className="bi bi-building"></i>
                    </div>
                    <h6 className="fw-bold">District</h6>
                    <p className="small text-muted mb-0">
                      District level analytics
                    </p>
                  </div>
                </div>
              </Col>}

              {userAllowedLevels.includes('ds-division') && <Col md={6} className="mb-3">
                <div
                  className={`level-option-card ${
                    selectedLevel === 'ds-division' ? 'selected' : ''
                  }`}
                  onClick={() => handleLevelChange('ds-division')}
                  style={{
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    border:
                      selectedLevel === 'ds-division'
                        ? '2px solid #667eea'
                        : '2px solid #e9ecef',
                    borderRadius: '10px',
                    padding: '20px',
                    background:
                      selectedLevel === 'ds-division'
                        ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
                        : 'white',
                  }}
                >
                  <div className="text-center">
                    <div style={{ fontSize: '2rem', marginBottom: '10px' }}>
                      <i className="bi bi-geo-alt-fill"></i>
                    </div>
                    <h6 className="fw-bold">DS Division</h6>
                    <p className="small text-muted mb-0">
                      Detailed area statistics
                    </p>
                  </div>
                </div>
              </Col>}
            </Row>

            {/* Province Selector */}
            {selectedLevel && selectedLevel !== 'all-island' && (
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold">
                  Select Province <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={selectedProvince}
                  onChange={handleProvinceChange}
                >
                  <option value="">Select Province</option>
                  {locationData.provinces.map((province) => (
                    <option key={province.code} value={province.code}>
                      {province.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            {/* District Selector */}
            {(selectedLevel === 'district' ||
              selectedLevel === 'ds-division') &&
              selectedProvince && (
                <Form.Group className="mb-4">
                  <Form.Label className="fw-bold">
                    Select District <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    value={selectedDistrict}
                    onChange={handleDistrictChange}
                  >
                    <option value="">Select District</option>
                    {locationData.districts[selectedProvince]?.map(
                      (district) => (
                        <option key={district.code} value={district.code}>
                          {district.name}
                        </option>
                      )
                    )}
                  </Form.Select>
                </Form.Group>
              )}

            {/* DS Division Selector */}
            {selectedLevel === 'ds-division' && selectedDistrict && (
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold">
                  Select DS Division <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={selectedDivision}
                  onChange={(e) => setSelectedDivision(e.target.value)}
                >
                  <option value="">Select DS Division</option>
                  {locationData.divisions[selectedDistrict]?.map((division) => (
                    <option key={division.code} value={division.code}>
                      {division.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            {/* Selection Preview */}
            {getSelectedNames().length > 0 && (
              <div
                style={{
                  background: '#f8f9fa',
                  borderRadius: '8px',
                  padding: '20px',
                  marginTop: '20px',
                }}
              >
                <strong className="d-block mb-2">Your Selection:</strong>
                <div>
                  {getSelectedNames().map((item, index) => (
                    <span
                      key={index}
                      style={{
                        display: 'inline-block',
                        background: '#e7f3ff',
                        color: '#0066cc',
                        padding: '8px 15px',
                        borderRadius: '20px',
                        margin: '5px',
                        fontWeight: '500',
                      }}
                    >
                      {item.label}: {item.value}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* View Analytics Button */}
            <div className="d-grid mt-4">
              <Button
                variant="primary"
                size="lg"
                onClick={handleViewAnalytics}
                disabled={!isSelectionComplete()}
              >
                View Analytics
              </Button>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
}

export default AnalyticsSelection;
